﻿// ── Per-Tab Sidebar Configuration ──
// Used by page.tsx to render the correct sidebar for each top-level tab

export const TAB_KEYS = ['introduction', 'video', 'application', 'certification', 'report', 'materials'] as const;
export type TabKey = (typeof TAB_KEYS)[number];

export const TAB_LABELS: Record<TabKey, string> = {
  video: '成果视频',
  introduction: '成果简介',
  application: '申报书',
  certification: '成果鉴定',
  report: '成果报告',
  materials: '支撑材料',
};

export const TAB_ICONS: Record<TabKey, string> = {
  video: 'ri-video-line',
  introduction: 'ri-file-text-line',
  application: 'ri-article-line',
  certification: 'ri-shield-check-line',
  report: 'ri-bar-chart-box-line',
  materials: 'ri-folder-2-line',
};

export interface TabSidebarConfig {
  sidebarItems: SidebarNavItem[];
  defaultSectionId?: string;
}

// ═══════ TAB SIDEBAR MAP ═══════
export const tabSidebarMap: Record<TabKey, TabSidebarConfig> = {
  // ── 成果简介 ──
  introduction: {
    sidebarItems: [
      {
        id: 'intro-group',
        title: '成果简介',
        type: 'group',
        defaultExpanded: true,
        items: [
          { id: 'intro-main', label: '成果简介', icon: 'ri-file-text-line' },
        ],
      },
    ],
    defaultSectionId: 'intro-main',
  },

  // ── 成果视频: single video ──
  video: {
    sidebarItems: [
      {
        id: 'vid-group',
        title: '成果视频',
        type: 'group',
        defaultExpanded: true,
        items: [
          { id: 'vid-overview', label: '成果总览视频', icon: 'ri-film-line' },
        ],
      },
    ],
    defaultSectionId: 'vid-overview',
  },

  // ── 申报书: group + singles ──
  application: {
    sidebarItems: [
      {
        id: 'app-form-group',
        title: '申报书',
        type: 'group',
        defaultExpanded: true,
        items: [
          { id: 'app-form', label: '申报书预览', icon: 'ri-file-list-3-line' },
          { id: 'app-awards', label: '成果曾获荣誉', icon: 'ri-medal-line' },
          { id: 'app-timeline', label: '起止时间', icon: 'ri-calendar-line' },
          { id: 'section-intro', label: '主要解决的教学问题', type: 'single', icon: 'ri-information-line' },
          { id: 'section-methods', label: '解决教学问题的方法', type: 'single', icon: 'ri-tools-line' },
          { id: 'section-innovation', label: '创新点', type: 'single', icon: 'ri-sparkling-line' },
          { id: 'section-promotion', label: '推广应用效果', type: 'single', icon: 'ri-share-circle-line' },
          { id: 'section-contributors', label: '主要完成人情况', type: 'single', icon: 'ri-user-settings-line' },
          { id: 'section-units', label: '主要完成单位情况', type: 'single', icon: 'ri-building-line' },
          { id: 'section-recommend', label: '推荐单位意见', type: 'single', icon: 'ri-thumb-up-line' },
           ],
      },
    ],
    defaultSectionId: 'app-form',
  },

  // ── 成果鉴定 ──
  certification: {
    sidebarItems: [
      {
        id: 'cert-group',
        title: '成果鉴定',
        type: 'group',
        defaultExpanded: true,
        items: [
          { id: 'cert-assoc', label: '中国建设教育协会', icon: 'ri-shield-check-line' },
          { id: 'cert-edu', label: '江西省教育厅', icon: 'ri-government-line' },
        ],
      },
    ],
    defaultSectionId: 'cert-assoc',
  },

  // ── 支撑材料: 1 big group with 16 items ──
  materials: {
    sidebarItems: [
      {
        id: 'mat-group',
        title: '支撑材料',
        type: 'group',
        defaultExpanded: true,
        items: [
          { id: 'mat-awards', label: '曾获荣誉', icon: 'ri-medal-line' },
          { id: 'mat-honors', label: '专业所获荣誉', icon: 'ri-award-line' },
          { id: 'mat-curriculum', label: '课程建设', icon: 'ri-book-open-line' },
          { id: 'mat-textbooks', label: '教材建设', icon: 'ri-stack-line' },
          { id: 'mat-platform', label: '平台建设', icon: 'ri-server-line' },
          { id: 'mat-faculty', label: '师资队伍建设', icon: 'ri-team-line' },
          { id: 'mat-party', label: '党建思政建设', icon: 'ri-flag-line' },
          { id: 'mat-reform-projects', label: '代表性教改项目', icon: 'ri-rocket-line' },
          { id: 'mat-industry-cooperation', label: '产教合作基地', icon: 'ri-exchange-line' },
          { id: 'mat-international', label: '国际化人才培养', icon: 'ri-earth-line' },
          { id: 'mat-excellence', label: '卓越人才培养', icon: 'ri-star-smile-line' },
          { id: 'mat-student-awards', label: '学生学科竞赛获奖', icon: 'ri-trophy-line' },
          { id: 'mat-student-innovation', label: '学生创新创业项目', icon: 'ri-lightbulb-line' },
          { id: 'mat-outstanding-graduates', label: '优秀毕业生典型', icon: 'ri-star-line' },
          { id: 'mat-enterprise-evaluation', label: '企业评价', icon: 'ri-feedback-line' },
          { id: 'mat-university-evaluation', label: '高校评价', icon: 'ri-building-2-line' },
          { id: 'mat-social-impact', label: '社会影响', icon: 'ri-newspaper-line' },
        ],
      },
    ],
    defaultSectionId: 'mat-honors',
  },

  // ── 成果报告: group with 6 items ──
  report: {
    sidebarItems: [
      {
        id: 'rep-group',
        title: '成果报告',
        type: 'group',
        defaultExpanded: true,
        items: [
          { id: 'rep-background', label: '背景及问题提出', icon: 'ri-history-line' },
          { id: 'rep-history', label: '专业建设历程', icon: 'ri-timeline-line' },
          { id: 'rep-approach', label: '解决问题的总体思路', icon: 'ri-route-line' },
          { id: 'rep-content', label: '主要内容与解决方法', icon: 'ri-file-list-line' },
          { id: 'rep-innovation', label: '主要创新点', icon: 'ri-flashlight-line' },
          { id: 'rep-promotion', label: '推广应用效果与反思', icon: 'ri-global-line' },
        ],
      },
    ],
    defaultSectionId: 'rep-background',
  },
};

export interface SidebarNavGroup {
  id: string;
  title: string;
  type: 'group';
  defaultExpanded?: boolean;
  items: { id: string; label: string; icon?: string }[];
}

export interface SidebarNavSingle {
  id: string;
  title: string;
  type: 'single';
  icon?: string;
}

export type SidebarNavItem = SidebarNavGroup | SidebarNavSingle;

export interface FileItem {
  name: string;
  fileType: 'pdf' | 'image' | 'video' | 'file';
  url: string;
}

export interface TextContentData {
  contentType: 'text';
  title: string;
  content: string;
}

export interface FilesContentData {
  contentType: 'files';
  title: string;
  files: FileItem[];
}

export type SectionContent = TextContentData | FilesContentData;

// ── Sidebar Navigation Structure (legacy) ──
export const sidebarNav: SidebarNavItem[] = [
  {
    id: 'supporting-materials',
    title: '支撑材料',
    type: 'group',
    defaultExpanded: true,
    items: [
      { id: 'mat-awards', label: '曾获荣誉', icon: 'ri-medal-line' },
      { id: 'mat-honors', label: '专业所获荣誉', icon: 'ri-award-line' },
      { id: 'mat-curriculum', label: '课程建设', icon: 'ri-book-open-line' },
      { id: 'mat-textbooks', label: '教材建设', icon: 'ri-stack-line' },
      { id: 'mat-platform', label: '平台建设', icon: 'ri-server-line' },
      { id: 'mat-faculty', label: '师资队伍建设', icon: 'ri-team-line' },
      { id: 'mat-party', label: '党建思政建设', icon: 'ri-flag-line' },
      { id: 'mat-reform-projects', label: '代表性教改项目', icon: 'ri-rocket-line' },
      { id: 'mat-industry-cooperation', label: '产教合作基地', icon: 'ri-exchange-line' },
      { id: 'mat-international', label: '国际化人才培养', icon: 'ri-earth-line' },
      { id: 'mat-excellence', label: '卓越人才培养', icon: 'ri-star-smile-line' },
      { id: 'mat-student-awards', label: '学生学科竞赛获奖', icon: 'ri-trophy-line' },
      { id: 'mat-student-innovation', label: '学生创新创业项目', icon: 'ri-lightbulb-line' },
      { id: 'mat-outstanding-graduates', label: '优秀毕业生典型', icon: 'ri-star-line' },
      { id: 'mat-enterprise-evaluation', label: '企业评价', icon: 'ri-feedback-line' },
      { id: 'mat-university-evaluation', label: '高校评价', icon: 'ri-building-2-line' },
      { id: 'mat-social-impact', label: '社会影响', icon: 'ri-newspaper-line' },
    ],
  },
  {
    id: 'application-form',
    title: '申报书',
    type: 'group',
    items: [
      { id: 'app-form', label: '申报书预览', icon: 'ri-file-list-3-line' },
      { id: 'app-awards', label: '成果曾获荣誉', icon: 'ri-medal-line' },
      { id: 'app-timeline', label: '起止时间', icon: 'ri-calendar-line' },
    ],
  },
  {
    id: 'section-intro',
    title: '主要解决的教学问题',
    type: 'single',
    icon: 'ri-information-line',
  },
  {
    id: 'section-methods',
    title: '解决教学问题的方法',
    type: 'single',
    icon: 'ri-tools-line',
  },
  {
    id: 'section-innovation',
    title: '创新点',
    type: 'single',
    icon: 'ri-sparkling-line',
  },
  {
    id: 'section-promotion',
    title: '推广应用效果',
    type: 'single',
    icon: 'ri-share-circle-line',
  },
  {
    id: 'section-contributors',
    title: '主要完成人情况',
    type: 'single',
    icon: 'ri-user-settings-line',
  },
  {
    id: 'section-units',
    title: '主要完成单位情况',
    type: 'single',
    icon: 'ri-building-line',
  },
  {
    id: 'section-recommend',
    title: '推荐单位意见',
    type: 'single',
    icon: 'ri-thumb-up-line',
  },
  {
    id: 'report',
    title: '成果报告',
    type: 'group',
    items: [
      { id: 'rep-background', label: '背景及问题提出', icon: 'ri-history-line' },
      { id: 'rep-history', label: '专业建设历程', icon: 'ri-timeline-line' },
      { id: 'rep-approach', label: '解决问题的总体思路', icon: 'ri-route-line' },
      { id: 'rep-content', label: '主要内容与解决方法', icon: 'ri-file-list-line' },
      { id: 'rep-innovation', label: '主要创新点', icon: 'ri-flashlight-line' },
      { id: 'rep-promotion', label: '推广应用效果与反思', icon: 'ri-global-line' },
    ],
  },
];

// ── Content Map (keyed by sidebar item id) ──

const mockFiles: FileItem[] = [
  { name: '相关文件材料（一）', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/最终版.pdf' },
  { name: '佐证材料汇编', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/最终版.pdf' },
  { name: '成果证明材料扫描件', fileType: 'image', url: '#' },
  { name: '现场照片集锦', fileType: 'image', url: '#' },
  { name: '成果展示视频', fileType: 'video', url: '#' },
];

// Real PDF attachment URL
const REAL_PDF_URL = '/最终版.pdf';

export const contentMap: Record<string, SectionContent> = {
  // ── 成果简介 ──
  'intro-main': {
    contentType: 'text',
    title: '成果简介',
    content: `
<p style="text-indent:2em;text-align:justify;margin:0;font-size:20px">随着我国"交通强国、铁路先行"和铁路"走出去"等战略持续推进，交通基础设施建设高质量发展对行业人才提出了全方位、多层次和国际化需求，人才培养体系面临着从量到质的转型升级。</p>
<p style="text-indent:2em;text-align:justify;margin:0;font-size:20px">根据《华东交通大学党委关于深入推进解放思想、深化改革、开放办学、加快发展的若干意见》（华交党发〔2013〕56号）要求，学校以培养"下得去、吃得苦、留得住、干得好、上得来"的"五得"轨道交通特色高素质复合型人才为目标，通过"学科赋能、需求牵引、交叉融合"等系列举措，探索出了轨道交通特色土建类一流专业建设新模式，见图1。</p>
<img src="https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/intro-image.png" alt="成果简介示意图" style="width:100%;display:block;border-radius:8px;margin-top:24px;margin-bottom:24px;" />
<h3>核心路径</h3>
<p style="text-indent:2em;text-align:justify;margin:0;font-size:20px"><strong>学科赋能</strong>——以"火车头"党建为引领，通过构建学科带头人兼任专业负责人的双责一体联动机制，搭建教学示范中心与全国重点实验室互通的教研平台，实施"导师领航、研本接力"的研学联动牵引育人。实现"双责一体固本 → 平台互通搭台 → 研学联动通脉"的学科育人新路径。</p>
<p style="text-indent:2em;text-align:justify;margin:0;font-size:20px"><strong>需求牵引</strong>——依据产业需求重构专业方向，"校企+校际"联动开设"中铁国际"订单班和留学生班级对接国际需求，实施铁路特色班级选拔及"天佑学子培育计划"。形成"产业需求定方向 → 校企校际协同育人 → '新铁军'品牌铸魂培优"的育人链条。</p>
<p style="text-indent:2em;text-align:justify;margin:0;font-size:20px"><strong>交叉融合</strong>——多学科交叉融通打造"交通+""土木+"等复合型专业，产教融合强化实训实践教学资源，科教融汇赋能实践教学。构建"多学科融通建专业 → 产教融合赋能实训 → 科教融汇聚力科创"的融合发展机制。</p>
<h3>建设成效</h3>
<p style="text-indent:2em;text-align:justify;margin:0;font-size:20px">经13年探索实践，6个专业入选国家一流本科专业建设点，5个通过工程教育认证。已建成“国家级教学实验示范中心”等国家级平台4个；建成“全国高校黄大年式教师团队等国家级教研团队”2个，国家级一流课程16门。</p>
<p style="text-indent:2em;text-align:justify;margin:0;font-size:20px">近五年，获"挑战杯"学术科技作品竞赛国家级特等奖2项、中国国际大学生创新大赛国家级金奖5项、“詹天佑优秀班集体”等集体荣誉4项、“茅以升铁道希望之星”等个人荣誉29项。</p>
<p style="text-indent:2em;text-align:justify;margin:0;font-size:20px">培养学生获国家铁路局等大型央企高度认可，获中国教育报等多家国家媒体报道。</p>
`,
  },

  // ── 成果鉴定 ──
  'cert-assoc': {
    contentType: 'files',
    title: '中国建设教育协会',
    files: [
      { name: '中国建设教育协会鉴定', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/0630-teaching-results.pdf' },
    ],
  },
  'cert-edu': {
    contentType: 'files',
    title: '江西省教育厅',
    files: [
      { name: '江西省教育厅鉴定', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/jyt.pdf' },
    ],
  },

  // ── 成果视频 ──
  'vid-overview': {
    contentType: 'files',
    title: '成果视频',
    files: [
      { name: '教学成果总览宣传片', fileType: 'video', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/achievement-video.mp4' },
    ],
  },

  // ── 支撑材料 ──
  'mat-awards': {
    contentType: 'files',
    title: '曾获荣誉',
    files: [
      { name: '曾获荣誉', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/1.pdf' },
    ],
  },
  'mat-honors': {
    contentType: 'files',
    title: '专业所获荣誉',
    files: [
      { name: '专业所获荣誉', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/2.pdf' },
    ],
  },
  'mat-curriculum': {
    contentType: 'files',
    title: '课程建设',
    files: [
      { name: '课程建设', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/3.pdf' },
    ],
  },
  'mat-textbooks': {
    contentType: 'files',
    title: '教材建设',
    files: [
      { name: '教材建设', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/4.pdf' },
    ],
  },
  'mat-platform': {
    contentType: 'files',
    title: '平台建设',
    files: [
      { name: '平台建设', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/5.pdf' },
    ],
  },
  'mat-faculty': {
    contentType: 'files',
    title: '师资队伍建设',
    files: [
      { name: '师资队伍建设', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/6.pdf' },
    ],
  },
  'mat-party': {
    contentType: 'files',
    title: '党建思政建设',
    files: [
      { name: '党建思政建设', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/7.pdf' },
    ],
  },
  'mat-reform-projects': {
    contentType: 'files',
    title: '代表性教改项目',
    files: [
      { name: '代表性教改项目', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/8.pdf' },
    ],
  },
  'mat-industry-cooperation': {
    contentType: 'files',
    title: '产教合作基地',
    files: [
      { name: '产教合作基地', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/9.pdf' },
    ],
  },
  'mat-international': {
    contentType: 'files',
    title: '国际化人才培养',
    files: [
      { name: '国际化人才培养', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/10.pdf' },
    ],
  },
  'mat-excellence': {
    contentType: 'files',
    title: '卓越人才培养',
    files: [
      { name: '卓越人才培养', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/11.pdf' },
    ],
  },
  'mat-student-awards': {
    contentType: 'files',
    title: '学生学科竞赛获奖',
    files: [
      { name: '学生学科竞赛获奖', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/12.pdf' },
    ],
  },
  'mat-student-innovation': {
    contentType: 'files',
    title: '学生创新创业项目',
    files: [
      { name: '学生创新创业项目', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/13.pdf' },
    ],
  },
  'mat-outstanding-graduates': {
    contentType: 'files',
    title: '优秀毕业生典型',
    files: [
      { name: '优秀毕业生典型', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/14.pdf' },
    ],
  },
  'mat-enterprise-evaluation': {
    contentType: 'files',
    title: '企业评价',
    files: [
      { name: '企业评价', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/15.pdf' },
    ],
  },
  'mat-university-evaluation': {
    contentType: 'files',
    title: '高校评价',
    files: [
      { name: '高校评价', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/16.pdf' },
    ],
  },
  'mat-social-impact': {
    contentType: 'files',
    title: '社会影响',
    files: [
      { name: '社会影响', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/17.pdf' },
    ],
  },

  // ── 申报书 ──
  'app-form': {
    contentType: 'files',
    title: '申报书预览',
    files: [
      { name: '申报书封面及基本信息', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/sbs.pdf' },
    ],
  },
  'app-awards': {
    contentType: 'files',
    title: '成果曾获荣誉',
    files: [
      { name: '申报书-成果曾获荣誉', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/app-remake.pdf' },
      { name: '国家级教学成果二等奖证书', fileType: 'pdf', url: REAL_PDF_URL },
    ],
  },
  'app-timeline': {
    contentType: 'text',
    title: '起止时间',
    content: `<p style="text-indent:2em;text-align:justify;margin:0;font-size:20px"><strong>起始时间：</strong>2013年09月</p>
<p style="text-indent:2em;text-align:justify;margin:0;font-size:20px"><strong>完成时间：</strong>2018年12月</p>
<p style="text-indent:2em;text-align:justify;margin:0;font-size:20px"><strong>实践检验期：</strong>7.5年</p>`,
  },

  // ... rest of the content remains the same
  // ── 申报书 - 教学问题 ──
  'section-intro': {
    contentType: 'text',
    title: '主要解决的教学问题',
    content: `<p style="text-indent:2em;text-align:justify;margin:0;font-size:20px"><span style="color:#8b0101;font-weight:600">（1）专业建设与学科优势的融合不够充分</span>：教学与科研组织融合不够深入，教学与科研平台共建基础薄弱，科研反哺教学不够充分，学科资源及优势难以转化为教学资源。</p>
<p style="text-indent:2em;text-align:justify;margin:0;font-size:20px"><span style="color:#8b0101;font-weight:600">（2）专业定位与行业特色需求衔接不紧密</span>：传统土建类专业培养方向与轨道交通行业需求吻合度不够，服务"一带一路"复合型卓越人才培养特色不突出，专业定位未精准对接行业发展需求。</p>
<p style="text-indent:2em;text-align:justify;margin:0;font-size:20px"><span style="color:#8b0101;font-weight:600">（3）专业内涵与复合型人才的培养关联弱</span>：传统土建类专业学科交叉不强，与产业融合弱，复合型人才培养难度大，专业内涵建设与行业复合型人才培养要求衔接不畅。</p>`,
  },

  // ── 申报书 - 解决方法 ──
  'section-methods': {
    contentType: 'files',
    title: '解决教学问题的方法',
    files: [
      { name: '解决教学问题的方法', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/app-methods.pdf' },
    ],
  },

  // ── 申报书 - 创新点 ──
  'section-innovation': {
    contentType: 'text',
    title: '成果的创新点（不超过800字）',
    content: `<p style="text-indent:2em;text-align:justify;margin:0;font-size:20px"><strong>（1）提出了学科专业融合建设新模式：</strong>以学校"火车头"党建为引领，健全学科带头人兼任专业负责人双责一体联动机制，夯实学科-专业融合根基；推进"支部-系-所"一体化建设，整合国家级教学示范中心与全国重点实验室资源，实施仪器设备开放共享制度，搭建了创新实践育人平台，打通教研资源互通壁垒；依托"筑创工程"等特色科创品牌，实施"导师领航、研本接力"的联动牵引育人，贯通科学研究与实践教学脉络。由此提出了"双责一体固本→平台互通搭台→研学联动通脉"的学科专业融合建设新模式。</p>
    <p style="text-indent:2em;text-align:justify;margin:0;font-size:20px"><strong>（2）构建了"新铁军"品牌复合型人才分层分类培养体系：</strong>以新时代轨道交通行业需求为导向，动态调整专业定位，优化专业布局及方向，增设轨道交通及数智化特色课程，推动人才培养与行业发展同频共振；实施"校企+校际"联动跨国协同育人，打造国际化课程体系，落地"中铁国际"订单班、"工程管理国际"班和留学生特色班；做实学校"新铁军"育人品牌，以历史伟人为引领，设立"詹天佑""茅以升"特色班级，实施"天佑学子培育计划"，落实择优选拔、动态流转、书院育人、一课多师培养制度，开展铁色品牌铸魂的卓越人才培养。形成了"产业需求定专业方向，校企校际多方协同育人，铁色品牌铸魂培优"的"新铁军"品牌复合型人才分层分类培养体系。</p>
    <p style="text-indent:2em;text-align:justify;margin:0;font-size:20px"><strong>（3）打造了多学科交叉的创新复合型人才培养体系：</strong>发挥土木、交通、信息学科优势，打造"土木+""交通+""软件+"等7个多元融通复合型新专业及"智能建造"新工科专业；依托省级现代产业学院，结合铁路院校特色优势，实施"铁路院校-铁路企业-铁路局-铁路院所"多方协同，"分层次、分阶段、立体化"产教融合实践育人举措；推行科研项目及成果进实践，跨专业联合，校企联动，跨校评比的科教融汇育人举措。由此构建了"多学科融通建专业，产教融合赋能实训，科教融汇聚力科创"的创新复合型人才培养体系。</p>`,
  },

  // ── 申报书 - 推广应用效果 ──
  'section-promotion': {
    contentType: 'files',
    title: '推广应用效果',
    files: [
      { name: '推广应用效果', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/app-promotion.pdf' },
    ],
  },

  // ── 主要完成人情况 ──
  'section-contributors': {
    contentType: 'files',
    title: '主要完成人情况',
    files: [
      { name: '申报书-主要完成人情况', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/app-point.pdf' },
    ],
  },

  // ── 主要完成单位情况 ──
  'section-units': {
    contentType: 'files',
    title: '主要完成单位情况',
    files: [
      { name: '主要完成单位情况', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/app-units.pdf' },
    ],
  },

  // ── 成果报告 ──
  'rep-background': {
    contentType: 'files',
    title: '背景及问题提出',
    files: [
      { name: '背景及问题提出', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/report-1-background.pdf' },
    ],
  },
  'rep-history': {
    contentType: 'text',
    title: '专业建设历程',
    content: `<p style="text-indent:2em;text-align:justify;margin:0;font-size:20px">针对轨道交通特色土建类一流专业建设面临的问题，华东交通大学土建类专业以学校教育教学深化改革为契机，开启了一流专业建设的改革与探索，改革建设历程如图2.1。其中，改革期（2013-2018年）主要建成<span style="color:#EE0000;font-weight:bold">“土木工程国家实验级教学示范中心”</span>，培育<span style="color:#EE0000;font-weight:bold">江西省土建类专业首位“教育部长江学者特聘教授”</span>，并获批土木工程一级学科博士点（江西省唯一）；成效期（2019年至今）建成<span style="color:#EE0000;font-weight:bold">“全国高校黄大年式教师团队”</span><span style="color:#EE0000;font-weight:bold">“全国教育系统先进集体”</span>等国家级教学团队2个，建成江西省土建类专业唯一<span style="color:#EE0000;font-weight:bold">“山区土木工程安全与韧性全国重点实验室”</span>（江西省高校牵头仅2家）等国家级平台3个，获批国家一流专业建设点6个，获批国家级一流课程16门，学生获"挑战杯"学术科技作品竞赛国家级特等奖2项，中国国际大学生创新大赛（原"互联网+"）国家级金奖5项等标志性业绩，成效显著并值得推广。</p>
<div style="margin-top:16px;"><img src="https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/lic.png" alt="专业建设历程示意图" style="width:100%;border-radius:8px;display:block;" /><p style="text-align:center;font-size:14px;font-weight:bold;margin-top:8px;color:#333">图2.1 轨道交通特色土建类一流专业建设历程</p></div>`,
  },
  'rep-approach': {
    contentType: 'files',
    title: '解决问题的总体思路',
    files: [
      { name: '解决问题的总体思路', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/report-3-approach.pdf' },
    ],
  },
  'rep-content': {
    contentType: 'files',
    title: '主要内容与解决方法',
    files: [
      { name: '主要内容与解决方法', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/report-4-content.pdf' },
    ],
  },
  'rep-innovation': {
    contentType: 'files',
    title: '主要创新点',
    files: [
      { name: '主要创新点', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/report-5-innovation.pdf' },
    ],
  },
  'rep-promotion': {
    contentType: 'files',
    title: '推广应用效果与反思',
    files: [
      { name: '推广应用效果与反思', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/report-6-promotion.pdf' },
    ],
  },

  // ── 推荐单位意见 ──
  'section-recommend': {
    contentType: 'files',
    title: '推荐单位意见',
    files: [
      { name: '推荐单位意见', fileType: 'pdf', url: 'https://huadongjiaoda-tumu.oss-cn-beijing.aliyuncs.com/file/active/app-recommendation.pdf' },
    ],
  },
};